// 错误日志记录系统
interface ErrorInfo {
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  userId?: string;
  component?: string;
  props?: any;
  state?: any;
  error?: any;
}

class ErrorLogger {
  private errors: ErrorInfo[] = [];
  private maxErrors = 100; // 最多保存100个错误

  // 记录错误
  logError(error: any, component?: string, props?: any, state?: any) {
    try {
      const errorInfo: ErrorInfo = {
        timestamp: new Date().toISOString(),
        type: 'error',
        message: error?.message || String(error),
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        component,
        props,
        state,
        error
      };

      this.errors.unshift(errorInfo);
      
      // 保持错误数量在限制内
      if (this.errors.length > this.maxErrors) {
        this.errors = this.errors.slice(0, this.maxErrors);
      }

      // 输出到控制台
      console.error('🚨 错误记录:', errorInfo);
      
      // 保存到localStorage
      this.saveToStorage();
    } catch (e) {
      console.error('记录错误时发生异常:', e);
    }
  }

  // 记录警告
  logWarning(message: string, component?: string, data?: any) {
    const warningInfo: ErrorInfo = {
      timestamp: new Date().toISOString(),
      type: 'warning',
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      component,
      props: data
    };

    this.errors.unshift(warningInfo);
    
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    console.warn('⚠️ 警告记录:', warningInfo);
    this.saveToStorage();
  }

  // 记录信息
  logInfo(message: string, component?: string, data?: any) {
    const info: ErrorInfo = {
      timestamp: new Date().toISOString(),
      type: 'info',
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      component,
      props: data
    };

    this.errors.unshift(info);
    
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    console.log('ℹ️ 信息记录:', info);
    this.saveToStorage();
  }

  // 获取所有错误
  getErrors(): ErrorInfo[] {
    return [...this.errors];
  }

  // 获取错误统计
  getErrorStats() {
    const stats = {
      total: this.errors.length,
      errors: this.errors.filter(e => e.type === 'error').length,
      warnings: this.errors.filter(e => e.type === 'warning').length,
      info: this.errors.filter(e => e.type === 'info').length,
      recent: this.errors.slice(0, 10) // 最近10个
    };
    return stats;
  }

  // 清空错误记录
  clearErrors() {
    this.errors = [];
    localStorage.removeItem('errorLogger');
    console.log('🧹 错误记录已清空');
  }

  // 导出错误数据
  exportErrors() {
    const data = {
      timestamp: new Date().toISOString(),
      errors: this.errors,
      stats: this.getErrorStats()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-log-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // 保存到localStorage
  private saveToStorage() {
    try {
      localStorage.setItem('errorLogger', JSON.stringify(this.errors));
    } catch (error) {
      console.error('保存错误日志失败:', error);
    }
  }

  // 从localStorage加载
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('errorLogger');
      if (stored) {
        this.errors = JSON.parse(stored);
        console.log(`📁 加载了 ${this.errors.length} 个错误记录`);
      }
    } catch (error) {
      console.error('加载错误日志失败:', error);
    }
  }

  // 打印错误摘要
  printSummary() {
    const stats = this.getErrorStats();
    console.group('📊 错误日志摘要');
    console.log(`总记录数: ${stats.total}`);
    console.log(`错误数: ${stats.errors}`);
    console.log(`警告数: ${stats.warnings}`);
    console.log(`信息数: ${stats.info}`);
    console.log('最近错误:', stats.recent);
    console.groupEnd();
  }
}

// 创建全局实例
const errorLogger = new ErrorLogger();

// 加载之前保存的错误
errorLogger.loadFromStorage();

// 将错误记录器添加到全局对象，方便调试
(window as any).errorLogger = errorLogger;

export default errorLogger;
